import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSelection } from '@/contexts/SelectionContext';
import { useRealtimePostgresChanges } from './useRealtimePostgresChanges';

// Central source-of-truth for the GMC-access-lifecycle UI state.
// Backed by merchant_centers.access_status + scheduled_deletion_at +
// deletion_overridden_at + access_revoked_modal_dismissed_at (see migration
// 20260516120000_merchant_center_access_lifecycle.sql).
//
// Consumed by:
//   - AccessRevokedOverlay (blocking modal, mounted in App.tsx)
//   - AccessRevokedBanner  (persistent top banner, mounted in App.tsx)
//   - useOptimizationJob / useUploadJob (defense-in-depth in the trigger paths)
//   - FeedHealthDashboard / Workbench (Button disabled state)

export interface McAccessStatus {
  mcId: string | null;
  mcName: string | null;
  isAccessRevoked: boolean;
  isDeletionOverridden: boolean;
  shouldShowBlockingModal: boolean;
  shouldShowPersistentBanner: boolean;
  daysUntilDeletion: number | null;
  lastPermissionErrorAt: Date | null;
  scheduledDeletionAt: Date | null;
  isLoading: boolean;
}

interface McLifecycleRow {
  id: string;
  name: string | null;
  access_status: 'active' | 'permission_denied';
  last_permission_error_at: string | null;
  scheduled_deletion_at: string | null;
  deletion_overridden_at: string | null;
  access_revoked_modal_dismissed_at: string | null;
}

export function useMcAccessStatus(): McAccessStatus {
  const { selectedMerchantCenterId } = useSelection();
  const queryClient = useQueryClient();
  const queryKey = ['mc-access-lifecycle', selectedMerchantCenterId];

  const { data, isLoading } = useQuery({
    queryKey,
    enabled: !!selectedMerchantCenterId,
    queryFn: async (): Promise<McLifecycleRow | null> => {
      if (!selectedMerchantCenterId) return null;
      const { data, error } = await supabase
        .from('merchant_centers')
        .select('id, name, access_status, last_permission_error_at, scheduled_deletion_at, deletion_overridden_at, access_revoked_modal_dismissed_at')
        .eq('id', selectedMerchantCenterId)
        .maybeSingle();

      if (error) {
        console.error('useMcAccessStatus: failed to load MC lifecycle', error);
        return null;
      }
      return data as McLifecycleRow | null;
    },
    staleTime: 30_000,
  });

  // Realtime subscription for the selected MC. Fires whenever
  // process-product-sync flips access_status, the user toggles override, or
  // fetch-account-data heals the row.
  //
  // This only works because merchant_centers is a member of the publication
  // supabase_realtime. It was NOT, from the day this hook was written until
  // migration 20260726044017_realtime_lifecycle_tables.sql — the handler below never
  // fired once, so the banner and the blocking modal only ever reflected the state at
  // app start (staleTime 30s, no refetch on focus, Layout never remounts).
  // Do not remove the table from the publication.
  //
  // This hook is mounted by five components at once (AccessRevokedBanner,
  // AccessRevokedOverlay, WorkbenchUploadAction, useOptimizationJob, useUploadJob). With the
  // fixed topic `mc-access-<id>` all five shared ONE channel — RealtimeClient.channel()
  // returns the existing channel for a known topic — which accumulated five
  // postgres_changes bindings against a join payload carrying only one filter. The
  // join-ok reconciliation then found no server filter at index 1 and made the channel
  // unsubscribe itself; that is the join/phx_leave/phx_close churn seen in production.
  // Unmounting any one of the five also removed the channel for all of them, because
  // RealtimeClient._remove() filters by topic. useRealtimePostgresChanges now gives each
  // instance its own topic; read the mechanism note there before touching it.
  useRealtimePostgresChanges(
    selectedMerchantCenterId ? `mc-access-${selectedMerchantCenterId}` : null,
    selectedMerchantCenterId
      ? {
          event: 'UPDATE',
          table: 'merchant_centers',
          filter: `id=eq.${selectedMerchantCenterId}`,
        }
      : null,
    () => {
      queryClient.invalidateQueries({ queryKey });
    },
  );

  return useMemo<McAccessStatus>(() => {
    const isAccessRevoked = data?.access_status === 'permission_denied';
    const isDeletionOverridden = !!data?.deletion_overridden_at;
    const modalDismissed = !!data?.access_revoked_modal_dismissed_at;

    let daysUntilDeletion: number | null = null;
    let scheduledDeletionAt: Date | null = null;
    if (data?.scheduled_deletion_at) {
      scheduledDeletionAt = new Date(data.scheduled_deletion_at);
      const msLeft = scheduledDeletionAt.getTime() - Date.now();
      daysUntilDeletion = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
    }

    return {
      mcId: data?.id ?? null,
      mcName: data?.name ?? null,
      isAccessRevoked,
      isDeletionOverridden,
      // Modal: only when revoked, user hasn't overridden, and modal not already dismissed.
      shouldShowBlockingModal: isAccessRevoked && !isDeletionOverridden && !modalDismissed,
      // Banner: always when revoked (both before and after override decision).
      shouldShowPersistentBanner: isAccessRevoked,
      daysUntilDeletion,
      lastPermissionErrorAt: data?.last_permission_error_at ? new Date(data.last_permission_error_at) : null,
      scheduledDeletionAt,
      isLoading,
    };
  }, [data, isLoading]);
}
